import { Badge, Box, IconProps, styled, Text, Tooltip } from '@betterleap/ui';
import React from 'react';
import { Location, NavigateFunction } from 'react-router-dom';
import styles from './SidebarLink.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StyledNavLink = (styled as any)('div');

type StyledNavLinkProps = React.ComponentProps<typeof StyledNavLink>;

interface SidebarLinkProps {
  title: string;
  route: string;
  beta?: boolean;
  id?: string;
  icon?: string | JSX.Element;
  onClick?: () => void;
  hidden?: boolean;
  css?: StyledNavLinkProps['css'];
  collapsed?: boolean;
  handleNavigate?: NavigateFunction | ((url: string) => void);
  currentLocation?: Location | string;
}

const SidebarLink = ({
  id,
  title,
  route,
  beta,
  icon,
  onClick,
  hidden,
  css,
  handleNavigate,
  collapsed,
  currentLocation,
}: SidebarLinkProps) => {
  const isActive = (path: string): boolean => {
    if (!currentLocation) {
      return false;
    }

    if (typeof currentLocation === 'string') {
      return currentLocation?.includes(path);
    }
    return currentLocation?.pathname.includes(path);
  };

  if (hidden) return null;
  return (
    <Tooltip
      content={title}
      side='right'
      className={`${!collapsed ? styles.collapsed : ''}`}
    >
      <StyledNavLink
        id={id}
        className={isActive(route) ? 'text-[#3A83F7]' : ''}
        key={route}
        css={{
          display: 'flex',
          mt: 8,
          padding: '14px 16px',
          borderRadius: '$xl',
          alignItems: 'center',
          cursor: 'pointer',
          color: '$neutral-blue-700',
          justifyContent: collapsed ? 'center' : 'none',
          '&.active': {
            color: 'white',
            backgroundColor: '$primary',
            boxShadow: '5px 5px 10px rgba(58, 131, 247, 0.3);',
            '& path': {
              fill: 'white',
            },
          },
          mediaMd: {
            width: !collapsed ? '100%' : 48,
            overflow: !collapsed ? 'hidden' : 'unset',
            transition: 'width .4s ease',
          },
          ...css,
        }}
        onClick={() => {
          if (onClick) onClick();
          if (handleNavigate) handleNavigate(route);
        }}
      >
        {icon &&
          (React.isValidElement(icon) ? (
            <Box css={{ mr: collapsed ? 0 : 12 }}>
              {React.cloneElement<IconProps>(icon as JSX.Element)}
            </Box>
          ) : (
            <img
              src={icon as string}
              alt='icon'
              className={`mr-2 ${styles.sidebar_icon}`}
              aria-hidden
            />
          ))}
        {!collapsed && (
          <Text
            css={{ flex: 1, whiteSpace: 'nowrap' }}
            size='base'
            inherit
            dataCy={title}
          >
            {title}
          </Text>
        )}
        {beta && (
          <Badge
            css={{
              hidden: {
                display: 'none',
              },
              current: {
                backgroundColor: 'white',
                color: '$blue-600',
              },
            }}
            size='sm'
            aria-current={isActive(route) ?? false}
            aria-hidden={collapsed}
            shape='rectangle'
            variant='info'
          >
            BETA
          </Badge>
        )}
      </StyledNavLink>
    </Tooltip>
  );
};

export default SidebarLink;
